// Mersalg: ekstra tilvalg, som en medarbejder har solgt efter bestillingen (typisk på stedet), og som kunden har godkendt.
// Rapporten tæller pr. medarbejder i en periode (opgavens dato). Beløb i øre ekskl. moms, plus total inkl. moms.
import type { Booking, Profil, Tilvalg } from './typer.ts';

export type MersalgLinje = {
  booking_id: number; dato: string; ekstra_id: string; af_id: string; tilvalg: string;
  beloeb: number; total: number; status: 'afventer' | 'godkendt' | 'afvist';
};
export type MersalgRaekke = {
  id: string; navn: string; rolle: Profil['rolle'] | null;
  opgaver: number | null;      // steamerens opgaver i perioden (ikke for kontoret)
  tilbudt: number; godkendt: number; afvist: number; afventer: number;
  beloeb: number; total: number;   // godkendt mersalg
  aendretNed: number; aendretNedBeloeb: number;   // steameren skiftede til en billigere pakke (kunden godkendte) – aldrig negativt mersalg
  rate: number | null;             // godkendt / (godkendt + afvist)
  prOpgave: number | null;         // godkendt mersalg pr. opgave (ekskl. moms)
  andel: number | null;            // andel af opgaverne med mersalg
  top: { navn: string; antal: number; beloeb: number }[];
  linjer: MersalgLinje[];
};
export type MersalgRapport = { fra: string; til: string; raekker: MersalgRaekke[]; iAlt: Omit<MersalgRaekke, 'id' | 'navn' | 'rolle' | 'top' | 'linjer'> };

const TAELLER: Booking['status'][] = ['godkendt', 'i_gang', 'faerdig'];

export function beregnMersalg(a: {
  bookinger: Booking[]; profiler: Pick<Profil, 'id' | 'navn' | 'rolle' | 'steamer_id'>[]; tilvalg: Pick<Tilvalg, 'id' | 'navn'>[]; fra: string; til: string; kun?: string | null;
}): MersalgRapport {
  const { fra, til } = a;
  const navnPaa = (id: string) => a.tilvalg.find((t) => t.id === id)?.navn ?? id;
  const profilFor = (e: { af_id?: string | null; af: string }, b: Booking) =>
    (e.af_id && a.profiler.find((p) => p.id === e.af_id)) || a.profiler.find((p) => p.navn === e.af) || a.profiler.find((p) => p.steamer_id && p.steamer_id === b.steamer_id) || null;
  const raekker = new Map<string, MersalgRaekke>();
  const raekke = (id: string, navn: string, rolle: MersalgRaekke['rolle']) => {
    let r = raekker.get(id);
    if (!r) { r = { id, navn, rolle, opgaver: null, tilbudt: 0, godkendt: 0, afvist: 0, afventer: 0, beloeb: 0, total: 0, aendretNed: 0, aendretNedBeloeb: 0, rate: null, prOpgave: null, andel: null, top: [], linjer: [] }; raekker.set(id, r); }
    return r;
  };
  const iPerioden = a.bookinger.filter((b) => b.dato >= fra && b.dato <= til);
  // Alle steamere med opgaver kommer med, også dem uden mersalg (så man kan se, hvem der ikke sælger).
  for (const p of a.profiler) if (p.rolle === 'steamer' && p.steamer_id) {
    const opg = iPerioden.filter((b) => b.steamer_id === p.steamer_id && TAELLER.includes(b.status)).length;
    if (opg > 0) raekke(p.id, p.navn, p.rolle).opgaver = opg;
  }
  const medSalg = new Map<string, Set<number>>();
  for (const b of iPerioden) for (const e of b.ekstra ?? []) {
    if (e.status === 'annulleret') continue;
    const p = profilFor(e, b);
    const id = p?.id ?? 'ukendt:' + e.af;
    const r = raekke(id, p?.navn ?? e.af, p?.rolle ?? null);
    // Ændret ned (billigere pakke/tilvalg): tælles for sig og aldrig som (negativt) mersalg eller som et tilbud
    if (e.type === 'aendring' && e.beloeb < 0) { if (e.status === 'godkendt') { r.aendretNed++; r.aendretNedBeloeb += -e.beloeb; } continue; }
    r.tilbudt++;
    r.linjer.push({ booking_id: b.id, dato: b.dato, ekstra_id: e.id, af_id: id, tilvalg: e.tilvalg.map((t) => (t.antal > 1 ? `${t.antal} × ` : '') + navnPaa(t.tilvalg_id)).join(', '), beloeb: e.beloeb, total: e.total, status: e.status });
    if (e.status === 'godkendt') {
      r.godkendt++; r.beloeb += e.beloeb; r.total += e.total;
      (medSalg.get(id) ?? medSalg.set(id, new Set()).get(id)!).add(b.id);
      for (const t of e.tilvalg) {
        const n = navnPaa(t.tilvalg_id); const x = r.top.find((y) => y.navn === n);
        if (x) { x.antal += t.antal; x.beloeb += t.pris; } else r.top.push({ navn: n, antal: t.antal, beloeb: t.pris });
      }
    } else if (e.status === 'afvist') r.afvist++;
    else r.afventer++;
  }
  for (const r of raekker.values()) {
    r.rate = r.godkendt + r.afvist > 0 ? r.godkendt / (r.godkendt + r.afvist) : null;
    if (r.opgaver) { r.prOpgave = Math.round(r.beloeb / r.opgaver); r.andel = (medSalg.get(r.id)?.size ?? 0) / r.opgaver; }
    r.top.sort((x, y) => y.beloeb - x.beloeb);
    r.linjer.sort((x, y) => y.dato.localeCompare(x.dato) || y.booking_id - x.booking_id);
  }
  let liste = [...raekker.values()].sort((x, y) => y.beloeb - x.beloeb || (y.opgaver ?? 0) - (x.opgaver ?? 0) || x.navn.localeCompare(y.navn, 'da'));
  if (a.kun) liste = liste.filter((r) => r.id === a.kun);
  const sum = (f: (r: MersalgRaekke) => number) => liste.reduce((s, r) => s + f(r), 0);
  const opgaver = sum((r) => r.opgaver ?? 0), godkendt = sum((r) => r.godkendt), afvist = sum((r) => r.afvist), beloeb = sum((r) => r.beloeb);
  const medSalgI = liste.reduce((s, r) => s + (r.opgaver ? medSalg.get(r.id)?.size ?? 0 : 0), 0);
  return {
    fra, til, raekker: liste,
    iAlt: { opgaver, tilbudt: sum((r) => r.tilbudt), godkendt, afvist, afventer: sum((r) => r.afventer), beloeb, total: sum((r) => r.total),
      aendretNed: sum((r) => r.aendretNed), aendretNedBeloeb: sum((r) => r.aendretNedBeloeb),
      rate: godkendt + afvist > 0 ? godkendt / (godkendt + afvist) : null, prOpgave: opgaver ? Math.round(beloeb / opgaver) : null, andel: opgaver ? medSalgI / opgaver : null },
  };
}
